import { useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SidebarSeo, SpecialtySidebarSeoEditModal } from '@/admin/components';
import { AppContext } from '@/admin/providers';
import {
  getIncrementedString,
  getSlugText,
  isSlugUnique,
} from '@/admin/utils/helpers';
import { getSpecialtySlugsApi } from '@/admin/utils/helpers-api';
import { ISEOData, ISlugData, ISpecialtyValues } from '@/common/types';

export const SpecialtySidebarSeo = () => {
  const { t } = useTranslation();
  const { getValues, setValue, watch } = useFormContext<ISpecialtyValues>();
  const {
    getAccessToken,
    selectedUserId,
    selectedUser: { userType },
  } = useContext(AppContext);

  const [slugsData, setSlugsData] = useState<ISlugData[]>([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);

  const metaDescription = watch('metaDescription');
  const metaTitle = watch('metaTitle');
  const slug = watch('slug');
  const id = getValues('id');

  useEffect(() => {
    (async () => {
      try {
        const token = await getAccessToken();
        const slugsData = await getSpecialtySlugsApi(
          token,
          selectedUserId,
          userType
        );

        if (slugsData) setSlugsData(slugsData);
      } catch (error) {
        /*eslint-disable-next-line no-console*/
        console.error(error);
      }
    })();
  }, [selectedUserId, userType]);

  useEffect(() => {
    if (!isSlugUnique(slugsData, slug, id)) {
      setValue('slug', getIncrementedString(slug), { shouldDirty: false });
    }
  }, [slug, slugsData]);

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleOpenEditModal = () => {
    setIsEditModalOpen(true);
  };

  const handleSaveEditModal = async ({
    slug,
    title,
    description,
  }: ISEOData) => {
    const newSlug =
      slug || getSlugText(getValues('header')) || t('newSpecialty');

    setValue('slug', newSlug, { shouldDirty: true });
    setValue('metaTitle', title || t('newSpecialty'), { shouldDirty: true });
    setValue('metaDescription', description, { shouldDirty: true });
    setValue('isSEODefault', false);
    setIsEditModalOpen(false);
  };

  return (
    <SidebarSeo
      metaDescriptionLabel={t('specialtyPageMetaDescription')}
      metaDescriptionValue={metaDescription}
      metaTitleLabel={t('specialtyPageMetaTitle')}
      metaTitleValue={metaTitle}
      seoEditModal={
        <SpecialtySidebarSeoEditModal
          open={isEditModalOpen}
          slugsData={slugsData}
          handleCloseEditModal={handleCloseEditModal}
          handleSaveEditModal={handleSaveEditModal}
        />
      }
      slugLabel={t('specialtyPageSlug')}
      slugValue={slug}
      handleOpenEditModal={handleOpenEditModal}
    />
  );
};
